import { FormattedMessage } from 'locales/index'
import React from 'react'
import { ComposedRegisterFieldName, RegisterForm, registerHasErrors } from 'shared/components/common/RegisterForm'
import { Form, Modal, ModalFooter, ModalProps } from '@commutifi-fe/ui'
import { RegisterFormPayload } from 'shared/interfaces/Account'

type CreateAccountCallback = (accountData: RegisterFormPayload) => void
interface CreateAccountModalProps extends Omit<ModalProps, 'onOk'> {
  onCreate: CreateAccountCallback
  children?: React.ReactNode
}

function CreateAccountModal({
  onCreate,
  onCancel,
  okText,
  cancelText,
  styles,
  children,
  ...props
}: CreateAccountModalProps) {
  const [form] = Form.useForm<RegisterFormPayload>()
  // eslint-disable-next-line react/hook-use-state -- Used to force update
  const [, forceUpdate] = React.useState()

  // To disable submit button at the beginning.
  React.useEffect(() => {
    forceUpdate(undefined)
  }, [])

  const handleOnCancel: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    form.resetFields()
    onCancel && onCancel(e)
  }

  const handleOnOk: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault()

    try {
      const account = await form.validateFields()
      onCreate(account)
    } catch {
      // Let antd Form handle errors automatically
    }
  }

  return (
    // Here we create another Form on top of RegisterForm so we can control the modal
    // footer buttons state. To make sure we don't interfere with RegisterForm submit
    // we need to pass submitButton=null in props to RegisterForm
    <Form layout="vertical" form={form} hideRequiredMark>
      <Modal
        {...props}
        onCancel={onCancel}
        styles={{ ...styles, body: { ...styles?.body, paddingBottom: 0 } }}
        footer={
          <Form.Item noStyle shouldUpdate>
            {(formInstance) => (
              <ModalFooter
                okText={okText || <FormattedMessage id="components.registerScreen.form.button.submit" />}
                cancelText={cancelText || <FormattedMessage id="button.cancel" />}
                cancelButtonProps={{
                  onClick: handleOnCancel,
                  ghost: true,
                  className: 'u-font-weight-semi'
                }}
                okButtonProps={{
                  type: 'primary',
                  onClick: handleOnOk,
                  disabled: registerHasErrors(
                    formInstance,
                    new Set([ComposedRegisterFieldName.Email, ComposedRegisterFieldName.Password])
                  )
                }}
              />
            )}
          </Form.Item>
        }
      >
        {children}
        <RegisterForm form={form} className="u-margin-top--medium-2" actions={null}>
          <RegisterForm.EmailField />
          <RegisterForm.PasswordField />
        </RegisterForm>
      </Modal>
    </Form>
  )
}

export default CreateAccountModal
